import React from 'react';
import { observer } from 'mobx-react';
import { Typography, Link, Grid, List, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import { toJS } from 'mobx';
import { Form } from '@jht/react-library';
import {
  orgNameFormSchema,
  facilityFormSchema,
  productOrderDetailsFormSchema,
  netpulseOrderDetailsFormSchema,
} from '../../pages/DigitalOrders/order-summary-form-schemas';
import {
  visionProducts,
  products,
  integrations,
} from '../../libs/static/productsAndIntegrations.enum';
import { ProductPricing } from '../../components/Product/ProductPricing';
import { getProductSchemaAndDetails } from './utils';

export interface OrderSummaryReadoutProps {
  order: any;
  orderId: string;
  showLinks?: boolean;
}

const useStyles = makeStyles(theme => ({
  title: {
    width: '100%',
  },
  link: {
    width: '100%',
    marginBottom: theme.spacing(1),
    fontSize: theme.spacing(2),
  },
  noItemSelectedText: {
    padding: '8px 14px 14px',
  },
  productListItem: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
  },
  usdNote: {
    color: theme.palette.primary.main,
  },
  listItem: {
    '& > span': {
      fontWeight: 600,
    },
  },
  gridContainer: {
    width: 'calc(100% - 32px)',
  },
}));

export const OrderSummaryReadout: React.FC<OrderSummaryReadoutProps> = observer(
  ({ order, orderId, showLinks = true }) => {
    const theme = useTheme();
    const classes = useStyles(theme);
    const { digitalOrdersStore } = useMst();
    const orgName =
      order.organization.name ||
      digitalOrdersStore.organizationMap.get(order.organization.organizationId)?.name;
    const facilitiesFormSchema = facilityFormSchema(
      toJS(order.facility.facilities),
      toJS(digitalOrdersStore.facilityMap),
    );
    const facilityNames = order.facility.facilities.map(f =>
      f.name ? f.name : digitalOrdersStore.facilityMap.get(f.facilityId)?.name,
    );
    const facilityExistence = order.facility.facilities.map(f => !!f.facilityId);
    const selectedProducts = order.products?.productSelection || {};
    const selectedIntegrations = order.products?.integrations || {};
    const isVision = import.meta.env.VITE_DO_MODE === 'vision';
    const productOptions = isVision ? visionProducts : products;

    const generateListItem = (items, selectedItems) => {
      // eslint-disable-next-line
      return items.map(item => {
        if (selectedItems.hasOwnProperty(item.value)) {
          const { details, formSchema } = getProductSchemaAndDetails(order, item.value, {
            facilityNames,
            facilityExistence,
          });
          return (
            <ListItem key={item.value}>
              <div className={classes.productListItem}>
                <Grid item xs={4}>
                  <ListItemText className={classes.listItem} primary={item.label} />
                </Grid>
                {formSchema && (
                  <Grid item>
                    <Form onChange={null} onSubmit={null} schema={formSchema}>
                      <></>
                    </Form>
                  </Grid>
                )}
                <ProductPricing
                  productName={item.value}
                  subformValues={details}
                  facilities={order.facility.facilities}
                />
              </div>
            </ListItem>
          );
        }
      });
    };

    return (
      <>
        <Typography className={classes.title} variant="h5">
          Order Review
        </Typography>
        <Grid container className={classes.gridContainer}>
          {showLinks ? (
            <Link className={classes.link} href={`/digital-orders/${orderId}/organization`}>
              Organization
            </Link>
          ) : (
            <Typography className={classes.link}>Organization</Typography>
          )}
          <Form onChange={null} onSubmit={null} schema={orgNameFormSchema(orgName)}>
            <></>
          </Form>
        </Grid>
        <Grid container className={classes.gridContainer}>
          {showLinks ? (
            <Link className={classes.link} href={`/digital-orders/${orderId}/facilities`}>
              {order.facility.facilities.length > 1 ? 'Facilities' : 'Facility'}
            </Link>
          ) : (
            <Typography className={classes.link}>
              {order.facility.facilities.length > 1 ? 'Facilities' : 'Facility'}
            </Typography>
          )}
          <Form onChange={null} onSubmit={null} schema={facilitiesFormSchema}>
            <></>
          </Form>
        </Grid>
        <Grid container className={classes.gridContainer}>
          {showLinks ? (
            <Link className={classes.link} href={`/digital-orders/${orderId}/products`}>
              Products
            </Link>
          ) : (
            <Typography className={classes.link}>Products</Typography>
          )}
          <Typography className={classes.usdNote} variant="subtitle2">
            All prices are US list prices
          </Typography>
          {Object.keys(selectedProducts).length ? (
            <Grid item xs={12}>
              <List>{generateListItem(productOptions, selectedProducts)}</List>
            </Grid>
          ) : (
            <div className={classes.noItemSelectedText}>No Products Selected</div>
          )}
          {isVision ? (
            <></>
          ) : showLinks ? (
            <Link className={classes.link} href={`/digital-orders/${orderId}/products`}>
              Integrations
            </Link>
          ) : (
            <Typography className={classes.link}>Integrations</Typography>
          )}
          {isVision ? (
            <></>
          ) : Object.keys(selectedIntegrations).length ? (
            <Grid item xs={12}>
              <List>{generateListItem(integrations, selectedIntegrations)}</List>
            </Grid>
          ) : (
            <div className={classes.noItemSelectedText}>No Integrations Selected</div>
          )}
        </Grid>
        <Grid container className={classes.gridContainer}>
          {showLinks ? (
            <Link className={classes.link} href={`/digital-orders/${orderId}/product-details`}>
              Product Order Details
            </Link>
          ) : (
            <Typography className={classes.link}>Product Order Details</Typography>
          )}
          <Form
            onChange={null}
            onSubmit={null}
            schema={productOrderDetailsFormSchema(order.productDetails || {})}
          >
            <></>
          </Form>
        </Grid>
        {selectedProducts.WTN && (
          <Grid container className={classes.gridContainer}>
            {showLinks ? (
              <Link className={classes.link} href={`/digital-orders/${orderId}/product-details`}>
                EGYM Order Details
              </Link>
            ) : (
              <Typography className={classes.link}>Product Order Details</Typography>
            )}
            <Form
              onChange={null}
              onSubmit={null}
              schema={netpulseOrderDetailsFormSchema(order.productDetails)}
            >
              <></>
            </Form>
          </Grid>
        )}
      </>
    );
  },
);
